import { HideBannerWhenOpenGame } from "@components/utils/IframeDialog/IframeDialog";
import { Paths } from "@constants/paths";
import { useMemberSettings } from "@hooks/useMemberSettings";
import preferenceSlice from "@stores/preference/slices.ts";
import {
  BeforeInstallPromptEvent,
  MemberSettingDownloadBannerAction,
  PWAInstaller,
  useCustomEventsListener,
} from "kz-ui-sdk";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";
import DownloadBanner from "./DownloadBanner";
import ExternalInstaller from "./ExternalInstaller";

interface AppInstallerProps {}

const EXCLUDED_PATHS = [Paths.PRIVATE.DEPOSIT_CALLBACK, Paths.PUBLIC.MAINTENANCE];

const AppInstaller = ({}: AppInstallerProps) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const [hideBanner, setHideBanner] = useState(false);
  const { downloadBanner } = useMemberSettings();

  const location = useLocation();

  const isExcludedPath = useMemo(() => {
    return EXCLUDED_PATHS.some((path) => matchPath(path, location.pathname));
  }, [location.pathname]);

  const handleOnAppInstallable = useCallback(
    (isInstallable: boolean) => {
      dispatch(preferenceSlice.actions.setDisplayAppInstaller(isInstallable));
    },
    [dispatch],
  );

  const handleOnBeforeInstallPrompt = (e?: Event) => {
    dispatch(preferenceSlice.actions.setIsAppInstallable(true));
    if (e) dispatch(preferenceSlice.actions.setAppInstallerPrompt(e as BeforeInstallPromptEvent));
  };

  const handleOnInstalled = () => {
    dispatch(preferenceSlice.actions.setIsAppInstallable(false));
  };

  useCustomEventsListener({
    [HideBannerWhenOpenGame]: (event: { detail: { hide: boolean | ((prevState: boolean) => boolean) } }) =>
      setHideBanner(event.detail.hide),
  });

  // Check if this is an external URL case
  const isExternalUrl = downloadBanner?.action === MemberSettingDownloadBannerAction.OpenExternalUrl;

  if (isExcludedPath) {
    return null;
  }

  if (isExternalUrl) {
    return (
      <ExternalInstaller
        onInstallable={handleOnAppInstallable}
        externalUrl={downloadBanner?.externalUrl}
        instructionOptions={{
          openExtBrowser: {
            videoSrc: `/videos/help-open-ext-browser_${language}.mp4`,
            title: t("download app"),
          },
          openShareAddHomeScreen: {
            videoSrc: `/videos/help-share-add-homescreen_${language}.mp4`,
            title: t("download app"),
          },
        }}
      >
        {(onInstall, onDismiss) => (
          <DownloadBanner
            onAction={onInstall}
            onDismiss={onDismiss}
            hideBanner={hideBanner}
            actionLabel={t("add")}
          />
        )}
      </ExternalInstaller>
    );
  }

  // Default PWA installer behavior
  return (
    <PWAInstaller
      onBeforeInstallPrompt={handleOnBeforeInstallPrompt}
      onInstalled={handleOnInstalled}
      onInstallable={handleOnAppInstallable}
      instructionOptions={{
        openExtBrowser: {
          videoSrc: `/videos/help-open-ext-browser_${language}.mp4`,
          title: t("download app"),
        },
        openShareAddHomeScreen: {
          videoSrc: `/videos/help-share-add-homescreen_${language}.mp4`,
          title: t("download app"),
        },
      }}
      skipPwaCheck
    >
      {(onInstall, onDismiss) => (
        <DownloadBanner
          onAction={onInstall}
          onDismiss={onDismiss}
          hideBanner={hideBanner}
          actionLabel={t("add")}
        />
      )}
    </PWAInstaller>
  );
};

export default AppInstaller;
